import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { stripChapterPrefix } from '../lib/utils';

const LS_KEY = 'scribehaven_reading_progress';

interface ContinueReadingProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

interface ProgressItem {
  storyId: number;
  storyTitle: string;
  storySlug: string;
  storyCover: string | null;
  storyGenre: string;
  authorName: string;
  totalChapters: number;
  chapterId: number;
  chapterTitle: string;
  chapterNum: number;
  scrollPosition: number;
  percentComplete: number;
  updatedAt: string;
}

export function getLocalProgress(): ProgressItem | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveLocalProgress(data: Partial<ProgressItem>) {
  try {
    const existing = getLocalProgress();
    const merged = { ...existing, ...data, updatedAt: new Date().toISOString() };
    localStorage.setItem(LS_KEY, JSON.stringify(merged));
  } catch {}
}

export default function ContinueReading({ onNavigate, user }: ContinueReadingProps) {
  const [items, setItems] = useState<ProgressItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (user) {
          const data = await api.progress.continueReading();
          if (data.length > 0) {
            setItems(data);
            setLoading(false);
            return;
          }
        }

        const local = getLocalProgress();
        if (local && local.storyTitle && local.chapterId) {
          setItems([local]);
        }
      } catch {
        const local = getLocalProgress();
        if (local && local.storyTitle && local.chapterId) {
          setItems([local]);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading || items.length === 0) return null;

  const gradients = [
    'from-purple-600 to-blue-600',
    'from-rose-600 to-pink-600',
    'from-emerald-600 to-teal-600',
    'from-amber-600 to-orange-600',
    'from-cyan-600 to-blue-600',
  ];

  const handleResume = (item: ProgressItem) => {
    saveLocalProgress(item);
    onNavigate('reader', {
      chapterId: item.chapterId,
      storySlug: item.storySlug,
      chapterNum: item.chapterNum,
      resumeScroll: item.scrollPosition,
    });
  };

  if (items.length === 1) {
    const item = items[0];
    const gradient = gradients[item.storyId % gradients.length];

    return (
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-xl font-bold">Continue Reading</h2>
          <span className="px-2 py-0.5 bg-ink-600/20 text-ink-400 text-xs font-semibold rounded-full">
            Resume
          </span>
        </div>
        <button
          onClick={() => handleResume(item)}
          className="w-full text-left bg-gradient-to-r from-gray-900 to-gray-900/80 border border-gray-800 rounded-2xl p-5 hover:border-ink-500/50 transition-all group cursor-pointer"
        >
          <div className="flex items-center gap-5">
            <div className={`w-16 h-24 rounded-lg bg-gradient-to-br ${gradient} flex-shrink-0 overflow-hidden`}>
              {item.storyCover ? (
                <img src={item.storyCover} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-2">
                  <span className="text-white/80 text-[10px] font-bold text-center leading-tight">{item.storyTitle}</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-ink-400 font-medium">{item.storyGenre}</span>
                <span className="text-gray-600">·</span>
                <span className="text-xs text-gray-500">by {item.authorName}</span>
              </div>
              <h3 className="text-lg font-bold truncate group-hover:text-ink-400 transition-colors">
                {item.storyTitle}
              </h3>
              <p className="text-sm text-gray-400 mt-0.5">
                Chapter {item.chapterNum}: {stripChapterPrefix(item.chapterTitle)}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden max-w-[200px]">
                  <div
                    className="h-full bg-ink-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, Math.round((item.chapterNum / Math.max(item.totalChapters, 1)) * 100))}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  Ch. {item.chapterNum} of {item.totalChapters}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink-600 group-hover:bg-ink-500 rounded-xl font-semibold text-sm transition-colors">
                Jump Back In
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </div>
          </div>
        </button>
      </section>
    );
  }

  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xl font-bold">Continue Reading</h2>
        <span className="px-2 py-0.5 bg-ink-600/20 text-ink-400 text-xs font-semibold rounded-full">
          {items.length} stories
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(item => {
          const gradient = gradients[item.storyId % gradients.length];
          return (
            <button
              key={item.storyId}
              onClick={() => handleResume(item)}
              className="text-left bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-ink-500/50 transition-all group cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <div className={`w-12 h-[72px] rounded-lg bg-gradient-to-br ${gradient} flex-shrink-0 overflow-hidden`}>
                  {item.storyCover ? (
                    <img src={item.storyCover} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-1">
                      <span className="text-white/80 text-[8px] font-bold text-center leading-tight">{item.storyTitle}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold truncate group-hover:text-ink-400 transition-colors">
                    {item.storyTitle}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">by {item.authorName}</p>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    Ch. {item.chapterNum}: {stripChapterPrefix(item.chapterTitle)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-ink-500 rounded-full"
                        style={{ width: `${Math.min(100, Math.round((item.chapterNum / Math.max(item.totalChapters, 1)) * 100))}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-600">{item.chapterNum}/{item.totalChapters}</span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
